import React from "react";

import { Stack, IconButton, Box, Typography, Button, CircularProgress } from "@mui/joy";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { PhotoItem } from "../../types";

interface PhotoUploadProps {
  photos: PhotoItem[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onAddClick?: () => void;
  onDeleteClick?: () => void;
  onPhotoClick?: (index: number) => void;
  isUploading?: boolean;
  emptyMessage?: string;
}

export function PhotoUpload({
  photos,
  currentIndex,
  onIndexChange,
  onAddClick,
  onDeleteClick,
  onPhotoClick,
  isUploading,
  emptyMessage = "No photos yet",
}: PhotoUploadProps) {
  const currentPhoto = photos[currentIndex];

  const handlePrev = () => {
    onIndexChange(currentIndex > 0 ? currentIndex - 1 : photos.length - 1);
  };

  const handleNext = () => {
    onIndexChange(currentIndex < photos.length - 1 ? currentIndex + 1 : 0);
  };

  if (photos.length === 0) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{
          width: "100%",
          padding: 4,
          backgroundColor: "background.level1",
        }}
      >
        {isUploading ? (
          <CircularProgress size="sm" />
        ) : (
          <>
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
              {emptyMessage}
            </Typography>
            {onAddClick && (
              <Button
                variant="outlined"
                size="sm"
                startDecorator={<PhotoCameraIcon />}
                onClick={onAddClick}
              >
                Add Photos
              </Button>
            )}
          </>
        )}
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        bgcolor: "#000",
      }}
    >
      {/* Photo display */}
      <Box
        sx={{
          width: "100%",
          aspectRatio: "16/9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isUploading ? (
          <CircularProgress sx={{ color: "white" }} />
        ) : (
          <Box
            component="img"
            src={currentPhoto.src}
            alt={currentPhoto.alt}
            onClick={onPhotoClick ? () => onPhotoClick(currentIndex) : undefined}
            sx={{
              objectFit: "cover",
              width: "100%",
              height: "100%",
              cursor: onPhotoClick ? "pointer" : "default",
            }}
          />
        )}
      </Box>

      {/* Delete button - top right */}
      {onDeleteClick && (
        <IconButton
          variant="solid"
          size="sm"
          color="danger"
          onClick={onDeleteClick}
          data-testid="photo-delete-button"
          aria-label="Delete photo"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 2,
            backgroundColor: "rgba(200, 0, 0, 0.7)",
            "&:hover": { backgroundColor: "rgba(200, 0, 0, 0.9)" },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}

      {/* Add more photos button - top left */}
      {onAddClick && (
        <IconButton
          size="sm"
          onClick={onAddClick}
          aria-label="Add photos"
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 2,
            bgcolor: "rgba(0,0,0,0.5)",
            color: "white",
            borderRadius: "50%",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <AddIcon />
        </IconButton>
      )}

      {/* Navigation arrows */}
      {photos.length > 1 && (
        <>
          <IconButton
            variant="solid"
            size="sm"
            onClick={handlePrev}
            data-testid="photo-carousel-prev"
            aria-label="Previous photo"
            sx={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "white",
              borderRadius: "50%",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            variant="solid"
            size="sm"
            onClick={handleNext}
            data-testid="photo-carousel-next"
            aria-label="Next photo"
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "white",
              borderRadius: "50%",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </>
      )}

      {/* Counter bar at bottom */}
      <Typography
        level="body-xs"
        sx={{
          position: "absolute",
          bottom: 8,
          right: 8,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          color: "white",
          paddingX: 1,
          paddingY: 0.5,
          borderRadius: "sm",
          fontWeight: 600,
        }}
      >
        {currentIndex + 1} / {photos.length}
      </Typography>
    </Box>
  );
}
